import React, { useState, useEffect, useMemo } from "react";
import ImageUpload from './ImageUpload';
import type { Employee } from "../types/employee";
import { MOBILE_REGEX as PHONE_REGEX, AADHAR_REGEX, PAN_REGEX, IFSC_REGEX } from '../utils/regex';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: FormData) => Promise<void>;
  editData?: Employee | null;
}

// Validation patterns and messages (shared from utils)

const VALIDATION_MESSAGES = {
  required: "This field is required",
  email: "Please enter a valid email address",
  phone: "Please enter a valid 10-digit mobile number",
  aadhar: "Please enter a valid 12-digit Aadhar number",
  pan: "Please enter a valid PAN number",
  ifsc: "Please enter a valid IFSC code",
};

const EmployeeFormModal: React.FC<Props> = ({ isOpen, onClose, onSave, editData }) => {
  // ✅ useMemo prevents re-creation of initialState on each render
  const initialState = useMemo<Omit<Employee, "id" | "createdAt" | "updatedAt">>(
    () => ({
       firstName: "",
  surname: "",
  mobileNumber: "",
  fatherName: "",
  fatherMobileNumber: "",
  motherName: "",
  motherMobileNumber: "",
  identificationMark: "",
  workPlace: "",
  education: "",
  caste: "",
  pradhanName: "",
  prdhanMobile: "",
  policeStation: "",
  policeMobile: "",
  dateOfBirth: "",
  userPhoto: "",
  currentAddress: "",
  residentialAddress: "",
  birthCertificate: "",
  joiningDate: "",
  exitDate: "",

  // ID Proofs
  electionNumber: "",
  idProof: "",
  electionCard: "",
  aadharNumber: "",
  aadharCardPhoto: "",
  panNumber: "",
  panCardPhoto: "",
  panCardReturned: false,
  drivingLicense: "",
  vehicleDetail: "",
  passportDetail: "",

  // Bank Details
  bankName: "",
  bankBranch: "",
  accountNumber: "",
  ifscCode: "",
    bankDetailProof: "",

    // Reference 1
    refName1: "",
    refMobileNumber1: "",
    refDateOfBirth1: "",
    refFatherName1: "",
    refFatherMobile1: "",
    refMotherName1: "",
    refMotherMobile1: "",
    refAddress1: "",
    refAdharNumber1: "",
    refElectionCard1: "",
    refIdProof1: "",

    // Reference 2
    refName2: "",
    refMobileNumber2: "",
    refDateOfBirth2: "",
    refFatherName2: "",
    refFatherMobile2: "",
    refMotherName2: "",
    refMotherMobile2: "",
    refAddress2: "",
    refAdharNumber2: "",
    refElectionCard2: "",
    refIdProof2: "",
    }),
    []
  );

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const [age, setAge] = useState('');
  // helper to calculate age from YYYY-MM-DD string
  const calculateAge = (dob?: string | null) => {
    if (!dob) return 0;
    const b = new Date(dob);
    if (isNaN(b.getTime())) return 0;
    const today = new Date();
    let age = today.getFullYear() - b.getFullYear();
    const m = today.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < b.getDate())) {
      age--;
    }
    return age >= 0 ? age : 0;
  };

  useEffect(() => {
    if (editData) {
      const dobStr = editData.dateOfBirth
        ? new Date(editData.dateOfBirth).toISOString().split("T")[0]
        : "";

      // Merge with initialState and ensure undefined fields fall back to defaults
      const merged: any = { ...initialState };
      Object.keys(merged).forEach((k) => {
        // copy only provided values from editData (avoid undefined)
        // @ts-ignore
        if (editData[k] !== undefined && editData[k] !== null) {
          // @ts-ignore
          merged[k] = editData[k];
        }
      });

      merged.dateOfBirth = dobStr;
      const computedAge = calculateAge(dobStr);
      merged.age = computedAge;
      setAge(computedAge.toString());
      merged.joiningDate = editData.joiningDate
        ? new Date(editData.joiningDate).toISOString().split("T")[0]
        : "";
      merged.exitDate = editData.exitDate
        ? new Date(editData.exitDate).toISOString().split("T")[0]
        : "";

      setFormData(merged);
    }
  }, [editData, initialState]); // initialState is stable via useMemo

  // Controlled file states for image uploads (use shared ImageUpload component below)
  const [userPhotoFile, setUserPhotoFile] = useState<File | null>(null);
  const [birthCertificateFile, setBirthCertificateFile] = useState<File | null>(null);
  const [idProofFile, setIdProofFile] = useState<File | null>(null);
  const [electionCardFile, setElectionCardFile] = useState<File | null>(null);
  const [aadharCardFile, setAadharCardFile] = useState<File | null>(null);
  const [panCardFile, setPanCardFile] = useState<File | null>(null);

  // New upload fields
  const [bankDetailProofFile, setBankDetailProofFile] = useState<File | null>(null);
  const [ref1IdProofFile, setRef1IdProofFile] = useState<File | null>(null);
  const [ref2IdProofFile, setRef2IdProofFile] = useState<File | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "dateOfBirth") {
      // calculate and set age when date of birth changes
      const computedAge = calculateAge(value || "");
      setAge(computedAge.toString());
      setFormData((prev) => ({ ...prev, dateOfBirth: value, age: computedAge }));
      // return;
    }

    // keep previous behavior (age field no longer editable)
    setFormData((prev) => ({
      ...prev,
      [name]: name === "age" ? parseInt(value) || 0 : value,
    }));
  };

  // Validate form fields
  const validateForm = () => {
    const errors: Record<string, string> = {};

    // Required field validations
    if (!formData.firstName) errors.firstName = VALIDATION_MESSAGES.required;
    if (!formData.surname) errors.surname = VALIDATION_MESSAGES.required;
    if (!formData.mobileNumber) errors.mobileNumber1 = VALIDATION_MESSAGES.required;
    if (!formData.fatherName) errors.fatherName = VALIDATION_MESSAGES.required;
    if (!formData.dateOfBirth) errors.dateOfBirth = VALIDATION_MESSAGES.required;
    if (!formData.currentAddress) errors.currentAddress = VALIDATION_MESSAGES.required;
    if (!formData.joiningDate) errors.joiningDate = VALIDATION_MESSAGES.required;
    if (!formData.aadharNumber) errors.aadharNumber = VALIDATION_MESSAGES.required;
    // panNumber is no longer required
    if (!formData.bankName) errors.bankName = VALIDATION_MESSAGES.required;
    if (!formData.accountNumber) errors.accountNumber = VALIDATION_MESSAGES.required;
    if (!formData.ifscCode) errors.ifscCode = VALIDATION_MESSAGES.required;

    // Pattern validations
    // if (formData.email && !EMAIL_REGEX.test(formData.email)) {
    //   errors.email = VALIDATION_MESSAGES.email;
    // }
    if (!PHONE_REGEX.test(formData.mobileNumber)) {
      errors.mobileNumber = VALIDATION_MESSAGES.phone;
    }
    if (!AADHAR_REGEX.test(formData.aadharNumber)) {
      errors.aadharNumber = VALIDATION_MESSAGES.aadhar;
    }
    if (formData.panNumber && !PAN_REGEX.test(formData.panNumber)) {
      errors.panNumber = VALIDATION_MESSAGES.pan;
    }
    if (!IFSC_REGEX.test(formData.ifscCode)) {
      errors.ifscCode = VALIDATION_MESSAGES.ifsc;
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) =>
        data.append(key, value?.toString())
      );

      if (userPhotoFile) data.append("userPhoto", userPhotoFile);
      if (birthCertificateFile) data.append("birthCertificate", birthCertificateFile);
      if (idProofFile) data.append("idProof", idProofFile);
      if (electionCardFile) data.append("electionCard", electionCardFile);
      if (aadharCardFile) data.append("aadharCardPhoto", aadharCardFile);
      if (panCardFile) data.append("panCardPhoto", panCardFile);
      if (bankDetailProofFile) data.append("bankDetailProof", bankDetailProofFile);
      if (ref1IdProofFile) data.append("refIdProof1", ref1IdProofFile);
      if (ref2IdProofFile) data.append("refIdProof2", ref2IdProofFile);

      await onSave(data);
      onClose();
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData(initialState);
    setUserPhotoFile(null);
    setBirthCertificateFile(null);
    setIdProofFile(null);
    setElectionCardFile(null);
    setAadharCardFile(null);
    setPanCardFile(null);
    setBankDetailProofFile(null);
    setRef1IdProofFile(null);
    setRef2IdProofFile(null);
  };

  const getErrorDisplay = (fieldName: string) => {
    return errors[fieldName] ? (
      <p className="text-red-500 text-xs mt-1">{errors[fieldName]}</p>
    ) : null;
  };

  if (!isOpen) return null;



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col shadow-lg">
        {/* Header */}
        <div className="p-5 border-b border-gray-300 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-[#06402B]">
            {editData ? "Update Employee Details" : "Add New Employee"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <svg
              className="w-6 h-6 text-gray-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <div className="p-6 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* ===== PERSONAL DETAILS SECTION ===== */}
            <div className="border border-[#06402B] rounded-lg p-5 space-y-4">
              <h3 className="text-lg font-medium text-[#06402B] border-b pb-2">
                Personal Details
              </h3>

              <div className="grid grid-cols-3 gap-4">
                {/* Row 1 */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Name <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`input-field ${errors.firstName ? 'border-red-500' : ''}`}
                    placeholder="Enter Name"
                  />
                  {getErrorDisplay('firstName')}
                </label>

                {/* surname */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Surname <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    className={`input-field ${errors.surname ? 'border-red-500' : ''}`}
                    placeholder="Enter Surname"
                  />
                  {getErrorDisplay('surname')}
                </label>

                {/* mobileno */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mobile No. <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="tel"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={`input-field ${errors.mobileNumber ? 'border-red-500' : ''}`}
                    placeholder="Enter Mobile Number"
                  />
                  {getErrorDisplay('mobileNumber')}
                </label>

                {/* father's name */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Father’s Name <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    name="fatherName"
                    value={formData.fatherName}
                    onChange={handleChange}
                    className={`input-field ${errors.fatherName ? 'border-red-500' : ''}`}
                    placeholder="Enter Father’s Name"
                  />
                  {getErrorDisplay('fatherName')}
                </label>

                {/* father's mobileno */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Father's Mobile No. <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="tel"
                    name="fatherMobileNumber"
                    value={formData.fatherMobileNumber}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={`input-field ${errors.fatherMobileNumber ? 'border-red-500' : ''}`}
                    placeholder="Enter Father's Mobile No."
                  />
                  {getErrorDisplay('fatherMobileNumber')}
                </label>

                {/* Photo Upload (Row 2–4) */}
                <div className="row-span-2">
                  <ImageUpload
                    label="Add Photo"
                    existingPath={editData?.userPhoto}
                    file={userPhotoFile}
                    onFileChange={setUserPhotoFile}
                    required
                    divClass="h-32"
                    maxPreviewHeight="128px"
                    maxPreviewWidth="100%"
                  />
                </div>
                  {/* <ImageUploadField label={'Add Photo'} fileUpload={userPhoto} required ={true} divClass={'row-span-3'} /> */}


                {/* mother's name */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mother's Name <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    name="motherName"
                    value={formData.motherName}
                    onChange={handleChange}
                    className={`input-field ${errors.motherName ? 'border-red-500' : ''}`}
                    placeholder="Enter Mother's Name"
                  />
                  {getErrorDisplay('motherName')}
                </label>

                {/* mother's mobileno */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mother's Mobile No.
                  </span>
                  <input
                    type="tel"
                    name="motherMobileNumber"
                    value={formData.motherMobileNumber}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={`input-field ${errors.motherMobileNumber ? 'border-red-500' : ''}`}
                    placeholder="Enter Mother's Mobile No."
                  />
                  {getErrorDisplay('motherMobileNumber')}
                </label>

                 {/* Row 4–5: Identification Mark */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Identification Mark
                  </label>
                  <input
                    type="text"
                    name="identificationMark"
                    value={formData.identificationMark}
                    onChange={handleChange}
                    className={`input-field ${errors.identificationMark ? 'border-red-500' : ''}`}
                    placeholder="Enter Identification Mark"
                  />
                  {getErrorDisplay('identificationMark')}
                </div>

                {/* mother's name */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    WorkPlace <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    name="workPlace"
                    value={formData.workPlace}
                    onChange={handleChange}
                    className={`input-field ${errors.workPlace ? 'border-red-500' : ''}`}
                    placeholder="Enter Work Place"
                  />
                  {getErrorDisplay('workPlace')}
                </label>

                <div className="row-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Residential Address
                  </label>
                  <textarea
                    name="residentialAddress"
                    value={formData.residentialAddress}
                    onChange={handleChange}
                    rows={5}
                    className={`input-field resize-none ${errors.residentialAddress ? 'border-red-500' : ''}`}
                    placeholder="Enter Residential Address"
                  />
                  {getErrorDisplay('residentialAddress')}
                </div>

                {/* Row 3: Education | Caste */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Education
                  </span>
                  <input
                    type="text"
                    name="education"
                    value={formData.education}
                    onChange={handleChange}
                    className={`input-field ${errors.education ? 'border-red-500' : ''}`}
                    placeholder="Enter Education"
                  />
                  {getErrorDisplay('education')}
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Caste
                  </span>
                  <input
                    type="text"
                    name="caste"
                    value={formData.caste}
                    onChange={handleChange}
                    className={`input-field ${errors.caste ? 'border-red-500' : ''}`}
                    placeholder="Enter Caste"
                  />
                  {getErrorDisplay('caste')}
                </label>

                {/* Pradhan name */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Pradhan Name
                  </span>
                  <input
                    type="text"
                    name="pradhanName"
                    value={formData.pradhanName}
                    onChange={handleChange}
                    className={`input-field ${errors.pradhanName ? 'border-red-500' : ''}`}
                    placeholder="Enter Pradhan Name"
                  />
                  {getErrorDisplay('pradhanName')}
                </label>

                {/* Pradhan mobileno */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Pradhan Mobile No.
                  </span>
                  <input
                    type="tel"
                    name="prdhanMobile"
                    value={formData.prdhanMobile}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={`input-field ${errors.prdhanMobile ? 'border-red-500' : ''}`}
                    placeholder="Enter Pradhan Mobile Number"
                  />
                  {getErrorDisplay('prdhanMobile')}
                </label>

                {/*Current Address */}
                <div className="row-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="currentAddress"
                    value={formData.currentAddress}
                    onChange={handleChange}
                    rows={5}
                    className={`input-field resize-none ${errors.currentAddress ? 'border-red-500' : ''}`}
                    placeholder="Enter Current Address"
                  />
                  {getErrorDisplay('currentAddress')}
                </div>

                {/* Police station */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Police Station
                  </span>
                  <input
                    type="text"
                    name="policeStation"
                    value={formData.policeStation}
                    onChange={handleChange}
                    className={`input-field ${errors.policeStation ? 'border-red-500' : ''}`}
                    placeholder="Enter Police Station"
                  />
                  {getErrorDisplay('policeStation')}
                </label>

                {/* police mobileno */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Police Contact No.
                  </span>
                  <input
                    type="tel"
                    name="policeMobile"
                    value={formData.policeMobile}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={`input-field ${errors.policeMobile ? 'border-red-500' : ''}`}
                    placeholder="Enter Police Contact Number"
                  />
                  {getErrorDisplay('policeMobile')}
                </label>

                {/* Row 2: DOB | Age | Photo */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                  <span className="block text-sm font-medium text-gray-700">
                    Date of Birth <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className={`input-field ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                  />
                  
                  {getErrorDisplay('dateOfBirth')}
                  </div>
                  <div>
                  <span className="block text-sm font-medium text-gray-700">
                    age
                  </span>
                  <div className="mt-1">
                    <input
                      type="text"
                      value={age}
                      readOnly
                      className="input-field w-24"
                    />
                  </div>
                  </div>
                </div>

                {/* <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Age <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="number"
                    name="age"
                    value={formData.age || ""}
                    onChange={handleChange}
                    className={`input-field ${errors.age ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter Age"
                  />
                  {getErrorDisplay('age')}
                </label> */}

                {/* <div className="row-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Add Birth Certificate
                  </label>
                  <div
                    {...birthCertificate.getRootProps()}
                    className="flex justify-center border-2 border-dashed border-gray-300 rounded-md p-4 h-48 cursor-pointer"
                  >
                    <input {...birthCertificate.getInputProps()} />
                    {birthCertificate.previewUrl ? (
                      <img
                        src={birthCertificate.previewUrl}
                        alt="Birth Certificate"
                        className="object-cover h-full w-full rounded-md"
                      />
                    ) : (
                      <p className="text-gray-400 text-sm text-center m-auto">
                        Upload Birth Certificate
                      </p>
                    )}
                  </div>
                </div> */}

                {/* Row 7: Joining & Exit Date */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Joining Date <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="date"
                    name="joiningDate"
                    value={formData.joiningDate}
                    onChange={handleChange}
                    className={`input-field ${errors.joiningDate ? 'border-red-500' : ''}`}
                  />
                  {getErrorDisplay('joiningDate')}
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Exit Date
                  </span>
                  <input
                    type="date"
                    name="exitDate"
                    value={formData.exitDate}
                    onChange={handleChange}
                    className={`input-field ${errors.exitDate ? 'border-red-500' : ''}`}
                  />
                  {getErrorDisplay('exitDate')}
                </label>

                {/* <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Email
                  </span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`input-field ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter Email"
                  />
                  {getErrorDisplay('email')}
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mobile Number 2
                  </span>
                  <input
                    type="tel"
                    name="mobileNumber2"
                    value={formData.mobileNumber2}
                    onChange={handleChange}
                    className={`input-field ${errors.mobileNumber2 ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter Mobile Number 2"
                  />
                  {getErrorDisplay('mobileNumber2')}
                </label> */}
              </div>
            </div>

            {/* ===== CONTACT DETAILS ===== */}
            {/* <div className="border border-[#06402B] rounded-lg p-5 space-y-4">
              <h3 className="text-lg font-medium text-[#06402B] border-b pb-2">
                Contact Details
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Email
                  </span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`input-field ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter Email"
                  />
                  {getErrorDisplay('email')}
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mobile Number 1 <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="tel"
                    name="mobileNumber1"
                    value={formData.mobileNumber1}
                    onChange={handleChange}
                    className={`input-field ${errors.mobileNumber1 ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter Mobile Number 1"
                  />
                  {getErrorDisplay('mobileNumber1')}
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mobile Number 2
                  </span>
                  <input
                    type="tel"
                    name="mobileNumber2"
                    value={formData.mobileNumber2}
                    onChange={handleChange}
                    className={`input-field ${errors.mobileNumber2 ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter Mobile Number 2"
                  />
                  {getErrorDisplay('mobileNumber2')}
                </label>
              </div>
            </div> */}

            {/* ===== ID PROOFS ===== */}
            <div className="border border-[#06402B] rounded-lg p-5 space-y-4">
              <h3 className="text-lg font-medium text-[#06402B] border-b pb-2">
                ID Proofs
              </h3>
              <div className="grid grid-cols-3 gap-4">
                {/* Row 1–2: ID Proof & Election Card */}
                {/* <div className="row-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID Proof
                  </label>
                  <div
                    {...idProof.getRootProps()}
                    className="flex justify-center border-2 border-dashed border-gray-300 rounded-md p-4 h-36 cursor-pointer"
                  >
                    <input {...idProof.getInputProps()} />
                    {idProof.previewUrl ? (
                      <img
                        src={idProof.previewUrl}
                        alt="ID Proof"
                        className="object-cover h-full w-full rounded-md"
                      />
                    ) : (
                      <p className="text-gray-400 text-sm text-center m-auto">
                        Upload ID Proof
                      </p>
                    )}
                  </div>
                </div> */}

                {/* Aadhar Card Number */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Aadhar Card Number <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    name="aadharNumber"
                    value={formData.aadharNumber}
                    onChange={handleChange}
                    className={`input-field ${errors.aadharNumber ? 'border-red-500' : ''}`}
                    placeholder="Enter Aadhar Card Number (12 digits)"
                  />
                  {getErrorDisplay('aadharNumber')}
                </label>

                {/* PAN Card Number */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    PAN Card Number
                  </span>
                  <input
                    type="text"
                    name="panNumber"
                    value={formData.panNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, panNumber: e.target.value.toUpperCase() }))}
                    maxLength={10}
                    className={`input-field ${errors.panNumber ? 'border-red-500' : ''}`}
                    placeholder="Eg: ABCDE1234F"
                  />
                  {getErrorDisplay('panNumber')}
                </label>

                {/* Election Card Number */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Election Card Number
                  </span>
                  <input
                    type="text"
                    name="electionNumber"
                    value={formData.electionNumber}
                    onChange={handleChange}
                    maxLength={20}
                    className={`input-field ${errors.electionNumber ? 'border-red-500' : ''}`}
                    placeholder="Enter Election Card Number"
                  />
                  {getErrorDisplay('electionNumber')}
                </label>

                {/* Aadhar Card Upload */}
                <div className="col-span-1 mb-4">
                  <ImageUpload
                    label="Add Aadhar Card (Front and Back)"
                    existingPath={editData?.aadharCardPhoto}
                    file={aadharCardFile}
                    onFileChange={setAadharCardFile}
                    divClass="h-40"
                    maxPreviewHeight="160px"
                    maxPreviewWidth="100%"
                  />
                </div>

                 {/* Add PAN Card */}
                <div className="col-span-1">
                  <ImageUpload
                    label="Add PAN Card"
                    existingPath={editData?.panCardPhoto}
                    file={panCardFile}
                    onFileChange={setPanCardFile}
                    divClass="h-32 mb-8"
                    maxPreviewHeight="128px"
                    maxPreviewWidth="100%"
                  />
                  {/* PAN Card Returned */}
                  <div className="flex items-center space-x-4 mt-3">
                  <span className="block text-sm font-medium text-gray-700">
                    ITR Filed (PAN)
                  </span>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="panCardReturned"
                        checked={formData.panCardReturned === true}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            panCardReturned: true,
                          }))
                        }
                      />
                      <span>Yes</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="panCardReturned"
                        checked={formData.panCardReturned === false}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            panCardReturned: false,
                          }))
                        }
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>

                </div>

                      <div className="col-span-1 mb-4">
                  <ImageUpload
                    label="Election Card"
                    existingPath={editData?.electionCard}
                    file={electionCardFile}
                    onFileChange={setElectionCardFile}
                    divClass="h-40"
                    maxPreviewHeight="160px"
                    maxPreviewWidth="100%"
                  />
                </div>
                 {/* Driving License No. */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Driving License No.
                  </span>
                  <input
                    type="text"
                    name="drivingLicense"
                    value={formData.drivingLicense}
                    onChange={handleChange}
                    className={`input-field ${errors.drivingLicense ? 'border-red-500' : ''}`}
                    placeholder="Enter Driving License Number"
                  />
                  {getErrorDisplay('drivingLicense')}
                </label>

                {/* Vehicle Details */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Vehicle Details
                  </span>
                  <input
                    type="text"
                    name="vehicleDetail"
                    value={formData.vehicleDetail}
                    onChange={handleChange}
                    className={`input-field ${errors.vehicleDetail ? 'border-red-500' : ''}`}
                    placeholder="Enter vechile Detail"
                  />
                  {getErrorDisplay('vehicleDetail')}
                </label>

                {/* Passport Details */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Passport Details
                  </span>
                  <input
                    type="text"
                    name="passportDetail"
                    value={formData.passportDetail}
                    onChange={handleChange}
                    className={`input-field ${errors.passportDetail ? 'border-red-500' : ''}`}
                    placeholder="Enter Passport Detail"
                  />
                  {getErrorDisplay('passportDetail')}
                </label>
                
                
              </div>
            </div>

            {/* ===== BANK DETAILS ===== */}
            <div className="border border-[#06402B] rounded-lg p-5 space-y-4">
              <h3 className="text-lg font-medium text-[#06402B] border-b pb-2">
                Bank Details
              </h3>
              <div className="grid grid-cols-3 gap-4">
                {/* Row 1 */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Bank Name <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    maxLength={50}
                    className={`input-field ${errors.bankName ? 'border-red-500' : ''}`}
                    placeholder="Enter Bank Name"
                  />
                  {getErrorDisplay('bankName')}
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Branch Name
                  </span>
                  <input
                    type="text"
                    name="bankBranch"
                    value={formData.bankBranch}
                    onChange={handleChange}
                    className={`input-field ${errors.bankBranch ? 'border-red-500' : ''}`}
                    placeholder="Enter Branch Name"
                  />
                  {getErrorDisplay('bankBranch')}
                </label>

                <div className="col-span-1 row-span-2">
                  <ImageUpload
                    label="Bank Detail"
                    existingPath={editData?.bankDetailProof}
                    file={bankDetailProofFile}
                    onFileChange={setBankDetailProofFile}
                    divClass="h-32"
                    maxPreviewHeight="128px"
                    maxPreviewWidth="100%"
                  />
                </div>

                {/* Row 2 */}
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Account Number <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleChange}
                    maxLength={20}
                    className={`input-field ${errors.accountNumber ? 'border-red-500' : ''}`}
                    placeholder="Enter Account Number"
                  />
                  {getErrorDisplay('accountNumber')}
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    IFSC Code <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    name="ifscCode"
                    value={formData.ifscCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
                    maxLength={11}
                    className={`input-field ${errors.ifscCode ? 'border-red-500' : ''}`}
                    placeholder="Enter IFSC Code"
                  />
                  {getErrorDisplay('ifscCode')}
                </label>

                <div></div>
              </div>
            </div>

            {/* Reference Details-1*/}
            <div className="border border-[#06402B] rounded-lg p-5 space-y-4">
              <h3 className="text-lg font-medium text-[#06402B] border-b pb-2">
                Reference Person's Detail - 1
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Name
                  </span>
                  <input
                    type="text"
                    name="refName1"
                    value={formData.refName1}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mobile
                  </span>
                  <input
                    type="tel"
                    name="refMobileNumber1"
                    value={formData.refMobileNumber1}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter Mobile Number"
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Date of Birth
                  </span>
                  <input
                    type="date"
                    name="refDateOfBirth1"
                    value={formData.refDateOfBirth1}
                    onChange={handleChange}
                    className={`input-field ${errors.refDateOfBirth1 ? 'border-red-500' : ''}`}
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Father's Name
                  </span>
                  <input
                    type="text"
                    name="refFatherName1"
                    value={formData.refFatherName1}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Father's Mobile No.
                  </span>
                  <input
                    type="tel"
                    name="refFatherMobile1"
                    value={formData.refFatherMobile1}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter Father's Mobile No."
                    className="input-field"
                  />
                  {getErrorDisplay('refFatherMobile1')}
                </label>

                <div className="row-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <textarea
                    name="refAddress1"
                    value={formData.refAddress1}
                    onChange={handleChange}
                    rows={4}
                    className={`input-field resize-none ${errors.refAddress1 ? 'border-red-500' : ''}`}
                    placeholder="Add Address"
                  />
                  {getErrorDisplay('refAddress1')}
                </div>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mother's Name
                  </span>
                  <input
                    type="text"
                    name="refMotherName1"
                    value={formData.refMotherName1}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mother's Mobile No.
                  </span>
                  <input
                    type="tel"
                    name="refMotherMobile1"
                    value={formData.refMotherMobile1}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Adhar card No.
                  </span>
                  <input
                    type="text"
                    name="refAdharNumber1"
                    value={formData.refAdharNumber1}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Election card No.
                  </span>
                  <input
                    type="text"
                    name="refElectionCard1"
                    value={formData.refElectionCard1}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <div className="col-span-1 mb-2">
                  <ImageUpload
                    label="ID Proof"
                    existingPath={editData?.refIdProof1}
                    file={ref1IdProofFile}
                    onFileChange={setRef1IdProofFile}
                    divClass="h-32"
                    maxPreviewHeight="128px"
                    maxPreviewWidth="100%"
                  />
                </div>
              </div>
            </div>

             {/* Reference Details-1*/}
            <div className="border border-[#06402B] rounded-lg p-5 space-y-4">
              <h3 className="text-lg font-medium text-[#06402B] border-b pb-2">
                Reference Person's Detail - 2
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Name
                  </span>
                  <input
                    type="text"
                    name="refName2"
                    value={formData.refName2}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mobile
                  </span>
                  <input
                    type="tel"
                    name="refMobileNumber2"
                    value={formData.refMobileNumber2}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter Mobile Number"
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Date of Birth
                  </span>
                  <input
                    type="date"
                    name="refDateOfBirth2"
                    value={formData.refDateOfBirth2}
                    onChange={handleChange}
                    className={`input-field ${errors.refDateOfBirth2 ? 'border-red-500' : ''}`}
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Father's Name
                  </span>
                  <input
                    type="text"
                    name="refFatherName2"
                    value={formData.refFatherName2}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Father's Mobile No.
                  </span>
                  <input
                    type="tel"
                    name="refFatherMobile2"
                    value={formData.refFatherMobile2}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter Father's Mobile No."
                    className="input-field"
                  />
                  {getErrorDisplay('refFatherMobile2')}
                </label>

                <div className="row-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <textarea
                    name="refAddress2"
                    value={formData.refAddress2}
                    onChange={handleChange}
                    rows={4}
                    className={`input-field resize-none ${errors.refPersonAddress ? 'border-red-500' : ''}`}
                    placeholder="Add Address"
                  />
                  {getErrorDisplay('refAddress2')}
                </div>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mother's Name
                  </span>
                  <input
                    type="text"
                    name="refMotherName2"
                    value={formData.refMotherName2}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Mother's Mobile No.
                  </span>
                  <input
                    type="tel"
                    name="refMotherMobile2"
                    value={formData.refMotherMobile2}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Adhar card No.
                  </span>
                  <input
                    type="text"
                    name="refAdharNumber2"
                    value={formData.refAdharNumber2}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-gray-700">
                    Election card No.
                  </span>
                  <input
                    type="text"
                    name="refElectionCard2"
                    value={formData.refElectionCard2}
                    onChange={handleChange}
                    className="input-field"
                  />
                </label>

                <div className="col-span-1 mb-2">
                  <ImageUpload
                    label="ID Proof"
                    existingPath={editData?.refIdProof2}
                    file={ref2IdProofFile}
                    onFileChange={setRef2IdProofFile}
                    divClass="h-32"
                    maxPreviewHeight="128px"
                    maxPreviewWidth="100%"
                  />
                </div>

              </div>
            </div>

            {/* BUTTONS */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClear}
                className="px-5 py-2 text-sm font-medium text-[#06402B] border border-[#06402B] rounded-md hover:bg-[#06402B]/10"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-[#06402B] text-white text-sm font-medium rounded-md hover:bg-[#053421]"
                disabled={loading}
              >
                {editData ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmployeeFormModal;
